import { format, formatDistance, parse } from 'date-fns'

export type DateFormat = string
export type TimeFormat = '12h' | '24h'
export type NumberFormat = 'thousand_dot' | 'thousand_comma' | 'indian' | string

export interface FormatPreferences {
  dateFormat: DateFormat
  timeFormat: TimeFormat
  numberFormat: NumberFormat
}

const DATE_FORMAT_PATTERNS: Record<string, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY/MM/DD': 'yyyy/MM/dd',
  'DD.MM.YYYY': 'dd.MM.yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'MMM DD, YYYY': 'MMM dd, yyyy',
  'DD MMM YYYY': 'dd MMM yyyy',
  'MMMM DD, YYYY': 'MMMM dd, yyyy',
  'DD MMMM YYYY': 'dd MMMM yyyy'
}

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  TRY: '₺',
  INR: '₹',
  CNY: '¥',
  RUB: '₽',
  CAD: 'C$',
  AUD: 'A$',
  // Add more currencies as needed
};

export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount)
}

export function formatDateWithPrefs(date: Date | string, preferences: FormatPreferences): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const pattern = DATE_FORMAT_PATTERNS[preferences.dateFormat] || preferences.dateFormat
  return format(dateObj, pattern)
}

export function formatRelativeDate(date: Date | string): string {
  return formatDistance(new Date(date), new Date(), { addSuffix: true })
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function formatTimeWithPrefs(date: Date | string, preferences: FormatPreferences): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const pattern = preferences.timeFormat === '12h' ? 'h:mm a' : 'HH:mm'
  return format(dateObj, pattern)
}

export function formatDateTimeWithPrefs(date: Date | string, preferences: FormatPreferences): string {
  return `${formatDateWithPrefs(date, preferences)} ${formatTimeWithPrefs(date, preferences)}`
}

export function formatNumberWithPrefs(
  number: number,
  preferences: FormatPreferences,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
): string {
  const { numberFormat } = preferences
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options

  switch (numberFormat) {
    case 'thousand_dot':
      return number.toLocaleString('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      })

    case 'thousand_comma':
      return number.toLocaleString('de-DE', {
        minimumFractionDigits,
        maximumFractionDigits,
      })

    case 'indian':
      return number.toLocaleString('en-IN', {
        minimumFractionDigits,
        maximumFractionDigits,
      })

    default:
      return number.toLocaleString('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      })
  }
}

export function formatCurrencyPreferences(
  amount: number,
  currency: string,
  preferences: FormatPreferences
): string {
  const formattedNumber = formatNumberWithPrefs(Math.abs(amount), preferences)
  const sign = amount < 0 ? '-' : ''
  const symbol = CURRENCY_SYMBOLS[currency] || currency
  return `${sign}${symbol}${formattedNumber}`
}

// Parse a formatted date string back to a Date object
export function parseFormattedDate(dateString: string, preferences: FormatPreferences): Date {
  const pattern = DATE_FORMAT_PATTERNS[preferences.dateFormat] || preferences.dateFormat
  return parse(dateString, pattern, new Date())
}

/**
 * Format a date into a human-friendly string
 * @param date The date to format
 * @param formatPattern The date-fns format pattern
 * @returns A formatted string
 */
export function formatDate(date: Date | string | number, formatPattern = 'MMM d, yyyy'): string {
  if (!date) return 'Unknown'
  
  try {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    return format(dateObj, formatPattern)
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Unknown'
  }
}

/**
 * Safely formats a date for use in Supabase queries to avoid timezone issues
 * @param date Date to format (can be Date, string, or number)
 * @returns ISO string in UTC format without timezone information
 */
export function formatDateForSupabase(date: Date | string | number): string {
  if (!date) return '';
  
  try {
    // Convert to Date object if it's not already
    const dateObj = date instanceof Date ? date : new Date(date);
    
    // Get the UTC ISO string (e.g. "2023-10-20T14:30:00.000Z")
    const isoString = dateObj.toISOString();
    
    // Return just the date part (e.g. "2023-10-20")
    // This removes the time and timezone components that can cause issues
    return isoString.split('T')[0];
  } catch (error) {
    console.error('Error formatting date for Supabase:', error);
    return '';
  }
}

/**
 * Format a time into a human-friendly string
 * @param date The date to extract time from
 * @param formatPattern The date-fns format pattern
 * @returns A formatted time string
 */
export function formatTime(date: Date | string | number, formatPattern = 'h:mm a'): string {
  if (!date) return 'Unknown'
  
  try {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    return format(dateObj, formatPattern)
  } catch (error) {
    console.error('Error formatting time:', error)
    return 'Unknown'
  }
}

/**
 * Format a date and time together
 * @param date The date to format
 * @param formatPattern The date-fns format pattern
 * @returns A formatted date and time string
 */
export function formatDateTime(date: Date | string | number, formatPattern = 'MMM d, yyyy h:mm a'): string {
  if (!date) return 'Unknown'
  
  try {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    return format(dateObj, formatPattern)
  } catch (error) {
    console.error('Error formatting date time:', error)
    return 'Unknown'
  }
}

/**
 * Get a human-readable time difference (e.g. "5 days ago")
 * @param date The date to calculate difference from now
 * @param baseDate The reference date, defaults to now
 * @returns A human-readable string
 */
export function getRelativeTimeFromNow(date: Date | string | number, baseDate = new Date()): string {
  if (!date) return 'Unknown'
  
  try {
    // Ensure date is a Date object
    const dateObj = date instanceof Date ? date : new Date(date)
    return formatDistance(dateObj, baseDate, { addSuffix: true })
  } catch (error) {
    console.error('Error calculating relative time:', error)
    return 'Unknown'
  }
}

/**
 * Format a currency amount
 * @param amount The amount to format
 * @param currency The currency code
 * @param locale The locale to use for formatting
 * @returns A formatted currency string
 */
export function formatCurrencyAmount(amount: number, currency = 'USD', locale = 'en-US'): string {
  if (amount === undefined || amount === null) return ''
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (error) {
    console.error('Error formatting currency:', error)
    return `${amount} ${currency}`
  }
}

/**
 * Format a number with the specified number of decimal places
 * @param value The number to format
 * @param decimalPlaces The number of decimal places
 * @param locale The locale to use for formatting
 * @returns A formatted number string
 */
export function formatNumberValue(value: number, decimalPlaces = 2, locale = 'en-US'): string {
  if (value === undefined || value === null) return ''
  
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(value)
  } catch (error) {
    console.error('Error formatting number:', error)
    return value.toString()
  }
}

/**
 * Format a percentage
 * @param value The value to format as percentage
 * @param decimalPlaces The number of decimal places
 * @param locale The locale to use for formatting
 * @returns A formatted percentage string
 */
export function formatPercentageValue(value: number, decimalPlaces = 1, locale = 'en-US'): string {
  if (value === undefined || value === null) return ''
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(value / 100)
  } catch (error) {
    console.error('Error formatting percentage:', error)
    return `${value}%`
  }
}
